import { modalService } from "@whatslly/ui-components/modals";
import styles from "./Modals.module.scss";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocalStoreWithInjectedDependencies } from "@whatslly/commons/di";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { getJSON, postJSON } from "@whatslly/commons/apiUtils";
import { inject, injectable } from "inversify";
import { Button } from "@whatslly/ui-components/v2/Button";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { FormikErrors, FormikProps } from "formik";
import { Label } from "@whatslly/ui-components/v2/BaseElements/Label";
import { FormTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput";
import type { PartialRecord } from "@whatslly/commons/typescriptUtils";
import { useTranslation } from "react-i18next";
import { ModalButtons } from "@whatslly/ui-components/ModalButtons";
import type { Destination, CRMServerToServerSettingsOnDataCloud } from "@whatslly/types/messages-server/destinations";
import { GenerateCertificateResponse, GenerateCertificateResponseSuccess, DESTINATION_INTEGRATION_TYPES } from "@whatslly/types/messages-server/destinations";
import omit from "lodash/omit";
import pick from "lodash/pick";
import { observer } from "mobx-react-lite";
import { FormCheckBox } from "@whatslly/ui-components/v2/FormElements/FormCheckBox";
import { fromPromise, IPromiseBasedObservable } from "mobx-utils";
import { parseOrReturnError } from "@whatslly/commons/parseOrReturnError";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import type { DataCloudTokenBodyResponseType } from "@whatslly/types/AuthBodyRequest";
import { downloadBlob } from "@whatslly/ui-components/componentsUtils";
import { TFunction } from "i18next";
import { makeObservable, observable, action } from "mobx";
import { FormSelect } from "@whatslly/ui-components/v2/FormElements/FormSelect";
import type { IOrganizationStore } from "../../../services/IOrganizationStore";
import { InjectOrganizationStore } from "../../../services/IOrganizationStore";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { FormPasswordTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput/FormPasswordTextInput";
import type { DestinationFormProps } from "../utils/DestinationsListTypes";
import classNames from "classnames";

const SERVER_URLS = [
  { label: "login.salesforce.com", value: "https://login.salesforce.com" },
  { label: "test.salesforce.com", value: "https://test.salesforce.com" },
];

type SalesforceDataCloudDestination = Extract<Destination, { type: "salesforceDataCloud" }>;

@injectable()
class ServerToServerStore {
  generateCertificatePromise: IPromiseBasedObservable<GenerateCertificateResponse | void> | undefined;
  generateServerToServerEtokenPromise: IPromiseBasedObservable<DataCloudTokenBodyResponseType | void> | undefined;

  constructor(
    @inject(InjectUserStore) private readonly userStore: IUserStore,
    @inject(InjectOrganizationStore) private readonly orgStore: IOrganizationStore,
    @inject(FrontLogger) private readonly logger: FrontLogger
  ) {
    makeObservable(this, {
      generateCertificatePromise: observable,
      generateServerToServerEtokenPromise: observable,
      generateCertificate: action,
      generateSalesforceSererToServerEtoken: action,
    });
  }

  async generateCertificate(t: TFunction) {
    this.generateCertificatePromise = fromPromise(
      getJSON({
        urlSuffix: `/message/destinations/${await this.orgStore.orgId}/generate-certificate`,
        etoken: this.userStore.etoken ?? "",
      })
        .then((r) => parseOrReturnError<GenerateCertificateResponse>(r, this.logger))
        .then((r) => {
          if (r.success) {
            return r;
          }
          modalService.openModal(Toast, { text: t("destinations_page.generate_certificate_failed_toast"), type: "error" });
        })
        .catch(() => {
          modalService.openModal(Toast, { text: t("destinations_page.generate_certificate_failed_toast"), type: "error" });
        })
    );
  }
  generateSalesforceSererToServerEtoken(
    t: TFunction,
    {
      username,
      connectedAppConsumerKey,
      privateKey,
      serverUrl,
    }: {
      username?: string;
      connectedAppConsumerKey?: string;
      privateKey: string;
      serverUrl: "https://login.salesforce.com" | "https://test.salesforce.com";
    }
  ) {
    this.generateServerToServerEtokenPromise = fromPromise(
      postJSON({
        urlSuffix: `/sfdc/v2/auth/data-cloud-token`,
        body: JSON.stringify({
          username,
          privateKey,
          connectedAppConsumerKey,
          serverUrl,
        }),
      })
        .then((r) => parseOrReturnError<DataCloudTokenBodyResponseType>(r, this.logger))
        .then((r) => {
          if (r.success) {
            return r;
          }
          modalService.openModal(Toast, { text: t("destinations_page.test_connection_failed_toast"), type: "error" });
        })
        .catch(() => {
          modalService.openModal(Toast, { text: t("destinations_page.test_connection_failed_toast"), type: "error" });
        })
    );
  }

  downloadCertificate(certificate: string | undefined) {
    if (certificate) {
      downloadBlob(atob(certificate), "tuvis-cert.pem", "application/x-pem-file");
      return;
    }
    if (this.generateCertificatePromise?.state === "fulfilled" && this.generateCertificatePromise.value?.success === true) {
      downloadBlob(atob(this.generateCertificatePromise.value.certificate), "tuvis-cert.pem", "application/x-pem-file");
    }
  }
}

type ServerToServerFormDataTypeFields =
  | "username"
  | "connectedAppConsumerKey"
  | "storeMedia"
  | "serverUrl"
  | "connectorName"
  | "objectName"
  | "sf360InstanceUrl";
type ServerToServerFormDataType = Pick<CRMServerToServerSettingsOnDataCloud, ServerToServerFormDataTypeFields>;

const ServerToServerTab: React.FC<{
  onSubmitForm: DestinationFormProps<"salesforceDataCloud">["onSubmitForm"];
  settings?: CRMServerToServerSettingsOnDataCloud;
  state: ServerToServerStore;
}> = observer(({ onSubmitForm, settings, state }) => {
  const [formikErrorHasErrors, setFormikErrorHasErrors] = useState(true);
  const formikContext = useRef<FormikProps<ServerToServerFormDataType>>(null);
  const { t } = useTranslation();

  const validate = async (values: ServerToServerFormDataType) => {
    const errors: FormikErrors<PartialRecord<keyof ServerToServerFormDataType, string>> = {};
    if (!values.username) errors.username = t("field_is_required");
    if (!values.connectedAppConsumerKey) errors.connectedAppConsumerKey = t("field_is_required");
    if (!values.objectName) errors.objectName = t("field_is_required");
    if (!values.sf360InstanceUrl) errors.sf360InstanceUrl = t("field_is_required");
    if (!values.connectorName) errors.connectorName = t("field_is_required");

    setFormikErrorHasErrors(Object.keys(errors).length > 0);
    return errors;
  };

  const isTestSubmitDisabled = useMemo(() => {
    const certFromServer = state.generateCertificatePromise?.state === "fulfilled" && state.generateCertificatePromise?.value?.success;
    const certFromSettings = settings?.certificate;
    return (!certFromServer && !certFromSettings) || formikErrorHasErrors;
  }, [state.generateCertificatePromise?.state, formikErrorHasErrors]);

  const getFormValues = () => {
    const data = (formikContext.current?.values ?? {}) as ServerToServerFormDataType;
    return {
      ...data,
      serverUrl: typeof data.serverUrl === "object" && "value" in data.serverUrl ? (data.serverUrl as any).value : data.serverUrl,
    };
  };

  const getCertData = useCallback(() => {
    return state.generateCertificatePromise?.state === "fulfilled" && state.generateCertificatePromise.value?.success
      ? pick<GenerateCertificateResponseSuccess, "privateKey" | "certificate">(state.generateCertificatePromise.value ?? {}, [
          "privateKey",
          "certificate",
        ])
      : {
          certificate: settings?.certificate ? settings.certificate : "",
          privateKey: settings?.privateKey ? settings.privateKey : "",
        };
  }, [state.generateCertificatePromise?.value, state.generateCertificatePromise?.state, settings]);

  const getSubmitData = useCallback((): SalesforceDataCloudDestination => {
    const certData = getCertData();
    return {
      type: "salesforceDataCloud" as const,
      integrationType: DESTINATION_INTEGRATION_TYPES.MESSAGES,
      settings: {
        sf360tokens:
          state.generateServerToServerEtokenPromise?.state === "fulfilled" && state.generateServerToServerEtokenPromise.value?.success
            ? state.generateServerToServerEtokenPromise.value.sf360tokens
            : "",
        instanceUrl:
          state.generateServerToServerEtokenPromise?.state === "fulfilled" && state.generateServerToServerEtokenPromise.value?.success
            ? state.generateServerToServerEtokenPromise.value.instanceUrl
            : "",
        ...omit(getFormValues()),
        ...certData,
      },
    };
  }, [getCertData, state.generateServerToServerEtokenPromise?.state, state.generateServerToServerEtokenPromise?.value]);

  useEffect(() => {
    validate(getFormValues());
  }, []);

  const onSubmit = useCallback(async () => {
    state.generateSalesforceSererToServerEtoken(t, {
      ...getCertData(),
      ...omit(getFormValues()),
    });
    const res = await state.generateServerToServerEtokenPromise;
    if (res?.success) {
      onSubmitForm(getSubmitData());
    }
  }, []);

  return (
    <div>
      <div className={styles.serverToServer}>
        {settings?.certificate ||
        (state.generateCertificatePromise?.state === "fulfilled" && state.generateCertificatePromise.value?.success === true) ? (
          <Button
            testId={"download-certificate-button"}
            onClick={() => state.downloadCertificate(settings?.certificate ?? undefined)}
            paddingInline={"small"}
          >
            {t("destinations_page.download_certificate")}
          </Button>
        ) : (
          <Button
            testId={"generate-certificate-button"}
            isSubmitting={state.generateCertificatePromise?.state === "pending"}
            disabled={state.generateCertificatePromise?.state === "pending"}
            onClick={() => state.generateCertificate(t)}
            paddingInline={"small"}
          >
            {t("destinations_page.generate_certificate")}
          </Button>
        )}
      </div>
      <FormElement
        initialValues={
          settings
            ? {
                ...pick<CRMServerToServerSettingsOnDataCloud, ServerToServerFormDataTypeFields>(settings, [
                  "username",
                  "connectedAppConsumerKey",
                  "storeMedia",
                  "serverUrl",
                  "connectorName",
                  "objectName",
                  "sf360InstanceUrl",
                ]),
              }
            : {
                username: "",
                connectedAppConsumerKey: "",
                storeMedia: false,
                serverUrl: "https://login.salesforce.com",
                objectName: "",
                connectorName: "",
                sf360InstanceUrl: "",
              }
        }
        validate={validate}
        onSubmit={onSubmit}
        formikRef={formikContext}
      >
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.server_url")} required />
          <FormSelect<{ value: string; label: string }>
            name={"serverUrl"}
            paddingInline="none"
            options={SERVER_URLS}
            defaultValue={settings?.serverUrl ? (SERVER_URLS.find((opt) => opt.value === settings.serverUrl) ?? SERVER_URLS[0]) : SERVER_URLS[0]}
            isClearable={false}
            required
          />
        </div>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.connected_app_consumer_key")} required />
          <FormPasswordTextInput name="connectedAppConsumerKey" paddingInline="none" />
        </div>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.username")} required />
          <FormTextInput name="username" paddingInline="none" />
        </div>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.connector_name")} required />
          <FormTextInput name="connectorName" paddingInline="none" />
        </div>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.object_name")} required />
          <FormTextInput name="objectName" paddingInline="none" />
        </div>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.ingestion_endpoint")} required />
          <FormTextInput name="sf360InstanceUrl" paddingInline="none" placeholder="{unique-id}.c360a.salesforce.com" />
        </div>
      </FormElement>
      <ModalButtons
        testId={`sf-save-button`}
        submitText={
          state.generateServerToServerEtokenPromise?.state === "pending" ? t("destinations_page.test_connection") : t("destinations_page.save")
        }
        isSubmitDisabled={isTestSubmitDisabled || state.generateServerToServerEtokenPromise?.state === "pending"}
        onSubmit={() => formikContext.current?.submitForm()}
        hideCancel={true}
      />
    </div>
  );
});

export const SalesforceDataCloudModalForm: React.FC<DestinationFormProps<"salesforceDataCloud">> = ({ settings, onSubmitForm }) => {
  const state = useLocalStoreWithInjectedDependencies(ServerToServerStore);

  return <ServerToServerTab onSubmitForm={onSubmitForm} settings={settings} state={state} />;
};
